import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { Box, Typography } from '@mui/material';
import { MprcContainer, MprcJoinComponents, MprcLink } from 'components';
import { MprcNotice } from 'components/MprcNotice';
import { ClassroomViewDto, ITrainingViewDto, RegistrationIntoTrainingChecksResults, TrainingPreRequisiteOption, TrainingType } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import { useModalUserProfile } from 'providers/UserProfileProvider';
import { useTranslation } from 'react-i18next';
import { ITraininRegistrationProps } from 'views/lms/student/Registration';

///////////////////////////////////////////////////////////////////////////////

interface IProps extends ITraininRegistrationProps {
    classrooms: ClassroomViewDto[];
    registerSubordinates: boolean;
    selectedClassroom: number;
    registrationChecks: RegistrationIntoTrainingChecksResults | null;
}

///
export const RegistrationNoticeView = ({ training, readonly, classrooms, selectedClassroom, registrationChecks, registerSubordinates }: IProps) => {
    const { t } = useTranslation("lms");
    const { showUser } = useModalUserProfile();

    const tvData: ITrainingViewDto = selectedClassroom >= 0
        ? classrooms[selectedClassroom]
        : training!;

    if (training == null || readonly || registerSubordinates)
        return null;

    if (registrationChecks?.trainingAudiencePassed === false)
        return (
            <MprcContainer margin="dense">
                <MprcNotice
                    disableTypography
                    items={[
                        {
                            icon: faTriangleExclamation, iconColor: "warning",
                            title: <Typography variant="h4" color="warning.main">{t("Registration.Restrictions.AudienceNotice")}</Typography>,
                        }
                    ]}
                />
            </MprcContainer>
        );

    if (training.trainingType != TrainingType.LearningPath && registrationChecks?.uniqueClassroomUnavailable === true)
        return (
            <MprcContainer margin="dense">
                <MprcNotice
                    disableTypography
                    items={[
                        {
                            icon: faTriangleExclamation, iconColor: "warning",
                            title: <Typography variant="h4" color="warning.main">{t("Registration.Restrictions.NoTrainingNotice")}</Typography>,
                            text: t("Registration.Restrictions.NoTrainingMessage")
                        }
                    ]}
                />
            </MprcContainer>
        );

    if (registrationChecks?.nextEnrollDate != null)
        return (
            <MprcContainer margin="dense">
                <MprcNotice
                    disableTypography
                    items={[
                        {
                            icon: faTriangleExclamation, iconColor: "warning",
                            title: <Typography variant="h4" color="warning.main">{t("Registration.Restrictions.NextTryLimitMessageNotice")}</Typography>,
                            text: <Box sx={{ mt: 1 }}>
                                <Typography variant="h6">
                                    {t("Registration.Restrictions.NextTryLimitMessage")}
                                    <b>{Formaters.performaDate(registrationChecks!.nextEnrollDate)}</b>
                                </Typography>
                            </Box>
                        }
                    ]}
                />
            </MprcContainer>
        );

    if (registrationChecks?.enrollMaxTries != null)
        return (
            <MprcContainer margin="dense">
                <MprcNotice
                    disableTypography
                    items={[
                        {
                            icon: faTriangleExclamation, iconColor: "warning",
                            title: <Typography variant="h4" color="warning.main">{t("Registration.Restrictions.TriesLimitNotice", { count: registrationChecks!.enrollMaxTries })}</Typography>,
                            text: t("Registration.Restrictions.TriesLimitMessage")
                        }
                    ]}
                />
            </MprcContainer>
        );

    if (selectedClassroom > -1 && classrooms[selectedClassroom]?.trainingPreReqs != null)
        return (
            <MprcContainer margin="dense">
                <MprcNotice
                    disableTypography
                    items={[
                        {
                            icon: faTriangleExclamation, iconColor: "warning",
                            title: <Typography variant="h4" color="warning.main">{t("Registration.Restrictions.PreRequisitesNotice")}</Typography>,
                            text: <Box >
                                {
                                    classrooms[selectedClassroom]!.trainingPreReqs.map((item, index) => (
                                        <div key={index}>
                                            <b>{item.name}</b>
                                            <span>: </span>
                                            {
                                                item.preRequisiteStatus == TrainingPreRequisiteOption.Approved
                                                    ? t("Registration.Restrictions.NeedsApproval")
                                                    : item.preRequisiteStatus == TrainingPreRequisiteOption.Reproved
                                                        ? t("Registration.Restrictions.NeedsReproval")
                                                        : item.preRequisiteStatus == TrainingPreRequisiteOption.NotAttended
                                                            ? t("Registration.Restrictions.NeedsNotAttend")
                                                            : t("Registration.Restrictions.NeedsNotAttendOrReproval")

                                            }
                                        </div>
                                    ))
                                }
                            </Box>
                        }
                    ]}
                />
            </MprcContainer>
        );

    if (selectedClassroom > -1 && classrooms[selectedClassroom]?.trainingSeatsPassed === false)
        return (
            <MprcContainer margin="dense">
                <MprcNotice
                    disableTypography
                    items={[
                        {
                            icon: faTriangleExclamation, iconColor: "warning",
                            title: <Typography variant="h4" color="warning.main">{t("Registration.Restrictions.NoSeatsNotice")}</Typography>
                        }
                    ]}
                />
            </MprcContainer>
        );

    if (classrooms.length == 0)
        return (
            <MprcContainer margin="dense">
                <MprcNotice
                    disableTypography
                    items={[
                        {
                            icon: faTriangleExclamation, iconColor: "warning",
                            title: <Typography variant="h4" color="warning.main">{t("Registration.Restrictions.NoClassroomNotice")}</Typography>,
                        }
                    ]}
                />
            </MprcContainer>
        );

    if (selectedClassroom != -1 && tvData.approvers && tvData.approvers.length > 0)
        return (
            <MprcContainer margin="dense">
                <MprcNotice
                    items={[
                        {
                            icon: faCircleInfo, iconColor: "info",
                            text: <>
                                {t("Training.Literals.ApproverMessage")}
                                <span>&nbsp;</span>
                                <MprcJoinComponents
                                    separator={t("Training.Literals.ApproverMessageAnd")}
                                    components={tvData.approvers.map(item => <MprcLink variant="linkAction" onClick={() => showUser(item.userId)}>{item.name}</MprcLink>)}
                                />
                            </>
                        }
                    ]}
                />
            </MprcContainer>
        );

    if (selectedClassroom != -1 && tvData.nextTrainingId != null)
        return (
            <MprcContainer margin="dense">
                <MprcNotice
                    items={[
                        {
                            icon: faCircleInfo, iconColor: "info",
                            text: <>
                                {t("Training.Literals.NextTrainingMessage")}
                                <span>&nbsp;</span>
                                <b>{tvData.nextTrainingName}</b>
                            </>
                        }
                    ]}
                />
            </MprcContainer>
        );

    if (training.trainingType != TrainingType.LearningPath && registrationChecks?.canInterest)
        return (
            <MprcContainer margin="dense">
                <MprcNotice
                    disableTypography
                    items={[
                        {
                            icon: faTriangleExclamation, iconColor: "warning",
                            title: <Typography variant="h4" color="warning.main">{t("Registration.Restrictions.NoClassroomNotice")}</Typography>,
                        }
                    ]}
                />
            </MprcContainer>
        );

    if (registrationChecks?.masterEnrollmentTrainingChecks != null) {
        const masterEnrollmentChecks = registrationChecks.masterEnrollmentTrainingChecks;

        var message = "";

        if (masterEnrollmentChecks.isExclusiveAndFinalized)
            message = t("Registration.Restrictions.TrainingExclusiveLpFinalizedMessage", { name: masterEnrollmentChecks.trainingName });
        else if (masterEnrollmentChecks.linkInLearnPath) {
            message = masterEnrollmentChecks.isReenroll
                ? t("Registration.Restrictions.NewEnrollInLearnPathMessage", { name: masterEnrollmentChecks.trainingName })
                : t("Registration.Restrictions.EnrollInLearnPathMessage", { name: masterEnrollmentChecks.trainingName })
                ;
        }
        else
            return null;

        return (
            <MprcContainer margin="dense">
                <MprcNotice
                    disableTypography
                    items={[
                        {
                            icon: faTriangleExclamation, iconColor: "warning",
                            title: <Typography variant="h6" color="warning.main">{message}</Typography>,
                        }
                    ]}
                />
            </MprcContainer>
        );
    }

    return null;
};
